/**
 * Обвязка списка упоминаний для suggestion-плагина Tiptap.
 *
 * Плагин не рисует ничего сам: он отдаёт координаты курсора и ждёт, что попап
 * покажет и уберёт кто-то другой. Компонент монтируется в body — внутри
 * редактора его обрезал бы скролл композера.
 *
 * Позиционирование ручное, без floating-ui: попап всегда под курсором и
 * переворачивается вверх только у нижней кромки окна — этого хватает, а
 * лишняя зависимость во фронте стоит дороже.
 */
import type { Editor } from '@tiptap/core'
import { VueRenderer } from '@tiptap/vue-3'

import MentionList from './MentionList.vue'

export interface MentionUser {
  id: string
  name: string
  /** Ссылка на фото; пусто — в подсказке кружок с инициалами */
  avatar: string
}

interface SuggestionProps {
  editor: Editor
  items: MentionUser[]
  command: (item: { id: string; label: string }) => void
  clientRect?: (() => DOMRect | null) | null
}

const GAP = 4
const ESTIMATED_HEIGHT = 240

const place = (element: HTMLElement, rect: DOMRect | null) => {
  if (!rect) return

  const below = window.innerHeight - rect.bottom > ESTIMATED_HEIGHT

  element.style.left = `${rect.left}px`
  element.style.top = below
    ? `${rect.bottom + GAP}px`
    : `${Math.max(GAP, rect.top - ESTIMATED_HEIGHT - GAP)}px`
}

export const renderMentionList = () => {
  let renderer: VueRenderer | null = null
  let container: HTMLElement | null = null

  const mount = (props: SuggestionProps) => {
    // Редактор передаётся не ради него самого: VueRenderer берёт из него
    // контекст приложения, иначе у списка не будет ни i18n, ни темы.
    renderer = new VueRenderer(MentionList, { props, editor: props.editor })
    const element = renderer.element

    if (!element) return

    container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.zIndex = '1000'
    container.appendChild(element)
    document.body.appendChild(container)
    place(container, props.clientRect?.() ?? null)
  }

  const unmount = () => {
    container?.remove()
    renderer?.destroy()
    container = null
    renderer = null
  }

  return {
    onStart: (props: SuggestionProps) => mount(props),

    onUpdate: (props: SuggestionProps) => {
      renderer?.updateProps(props)

      if (container) place(container, props.clientRect?.() ?? null)
    },

    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'Escape') {
        unmount()

        return true
      }

      // Стрелки и Enter отдаём списку: пока попап открыт, они выбирают
      // кандидата, а не двигают курсор в документе.
      return Boolean(
        (renderer?.ref as { onKeyDown?: (event: KeyboardEvent) => boolean } | undefined)?.onKeyDown?.(
          event,
        ),
      )
    },

    onExit: unmount,
  }
}
